import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import SignalDetailView from '../DetailView/SignalDetailView';
import { getLastActiveDatesBulk, getOltIps } from '../../services/api';
import RelativeTime, { formatAbsoluteDate, getFreshnessLevel } from './RelativeTime';
import InfoIcon from '@mui/icons-material/Info';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// ===== Утилиты =====

// Функция для преобразования номера порта
const formatPortNumber = (portNumber) => {
  if (!portNumber || isNaN(portNumber)) return 'N/A';

  // Проверяем, является ли номер порта в ожидаемом диапазоне
  if (portNumber < 16779009) {
    return String(portNumber);
  }

  try {
    const port = Math.floor((portNumber - 16779009) / 256) + 1;
    const subPort = ((portNumber - 16779009) % 256) + 1;
    return `${port}/${subPort}`;
  } catch (error) {
    console.error('Error formatting port number:', error, 'value:', portNumber);
    return String(portNumber);
  }
};

// Функция для форматирования значения сигнала (БД хранит в сотых долях dBm)
const formatSignalValue = (value) => {
  if (!value) return -40;
  return value / 100;
};

// «Устаревший» замер (> 24ч назад) — для красной подсветки строки.
// Берём «свежесть» из RelativeTime: там же применяется коррекция таймзоны (-7ч),
// что и в formatAbsoluteDate, иначе сырой таймстамп уходит в будущее и подсветка
// срабатывает на 7ч раньше времени.
const isSignalStale = (dateString) => {
  if (!dateString) return false;
  return getFreshnessLevel(dateString).color === 'error';
};

const staleRowSx = {
  backgroundColor: 'error.light',
  '&:hover': {
    backgroundColor: 'error.main',
    opacity: 0.92,
  },
};

// Компактная строка разбивки портов по PON-веткам: «ветки: 1/16, 2/8, 3/24 …»
// (номер ветки / число портов). branches — { '1': 16, '2': 8, ... }.
// Ограничиваем 8 ветками, чтобы заголовок не разрастался; дальше «…».
const formatBranches = (branches) => {
  if (!branches) return '';
  const entries = Object.entries(branches)
    .sort((a, b) => Number(a[0]) - Number(b[0]));
  if (!entries.length) return '';
  const shown = entries.slice(0, 8).map(([br, cnt]) => `${br}/${cnt}`).join(', ');
  return entries.length > 8 ? `ветки: ${shown} …` : `ветки: ${shown}`;
};

const SignalsTable = ({ data, loading, onRowClick, onPageChange, totalRows, oltStats = {} }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastActiveDates, setLastActiveDates] = useState({});
  const [oltDescriptions, setOltDescriptions] = useState({});
  // Множество свёрнутых OLT-групп. По умолчанию все группы развёрнуты (пустое множество).
  const [collapsedOlt, setCollapsedOlt] = useState(() => new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const rowsWithoutSignal = data.filter(
      (row) =>
        formatSignalValue(row.signal_olt) === -0.4 ||
        formatSignalValue(row.signal_onu) === -0.4
    );

    if (!rowsWithoutSignal.length) {
      setLastActiveDates({});
      return;
    }

    let cancelled = false;
    const ports = rowsWithoutSignal.map((row) => ({
      olt_ip: row.olt_ip,
      port_number: row.port_number,
    }));

    getLastActiveDatesBulk(ports).then((dates) => {
      if (!cancelled) {
        setLastActiveDates(dates);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [data]);

  useEffect(() => {
    const fetchOltDescriptions = async () => {
      try {
        const oltIps = await getOltIps();
        const descriptionsMap = {};
        oltIps.forEach((olt) => {
          descriptionsMap[olt.ip] = olt.description;
        });
        setOltDescriptions(descriptionsMap);
      } catch (error) {
        console.error('Error fetching OLT descriptions:', error);
      }
    };
    fetchOltDescriptions();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    onPageChange(0);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const getSignalStatus = (value) => {
    const signalValue = formatSignalValue(value);
    // -0.4 — сырое -40; <= -40 — sentinel -4000 (сотые), «замера нет».
    if (signalValue === -0.4 || signalValue <= -40) {
      return {
        color: 'error',
        label: 'Нет сигнала',
        icon: <SignalCellularConnectedNoInternet0BarIcon />,
      };
    } else if (signalValue < -30) {
      return { color: 'warning', label: 'Слабый', icon: <SignalCellularAltIcon /> };
    } else if (signalValue < -25) {
      return { color: 'info', label: 'Средний', icon: <SignalCellularAltIcon /> };
    } else {
      return { color: 'success', label: 'Хороший', icon: <SignalCellularAltIcon /> };
    }
  };

  const getOltDescription = (ip) => oltDescriptions[ip] || ip;

  const toggleOlt = (ip) => {
    setCollapsedOlt((prev) => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip);
      else next.add(ip);
      return next;
    });
  };

  // Сортируем данные по OLT IP и порту для группировки
  const sortedData = useMemo(
    () =>
      [...data].sort((a, b) => {
        if (a.olt_ip !== b.olt_ip) {
          return a.olt_ip.localeCompare(b.olt_ip);
        }
        const portA = Math.floor((a.port_number - 16779009) / 256) + 1;
        const portB = Math.floor((b.port_number - 16779009) / 256) + 1;
        if (portA !== portB) return portA - portB;
        const subPortA = ((a.port_number - 16779009) % 256) + 1;
        const subPortB = ((b.port_number - 16779009) % 256) + 1;
        return subPortA - subPortB;
      }),
    [data]
  );

  // Группируем отсортированные строки по OLT.
  // Счётчики берём из серверного oltStats (по всем данным с учётом фильтров),
  // а не из строк текущей страницы — у OLT могут быть сотни портов, на страницу
  // попадает лишь limit (по умолчанию 100).
  // Каждая группа: { olt_ip, olt_description, rows[], portCount, noSignal, weak }
  const groupedData = useMemo(() => {
    const groups = [];
    let current = null;
    for (const item of sortedData) {
      if (!current || current.olt_ip !== item.olt_ip) {
        const stats = oltStats[item.olt_ip] || {};
        current = {
          olt_ip: item.olt_ip,
          olt_description: getOltDescription(item.olt_ip),
          rows: [],
          portCount: stats.port_count,
          noSignal: stats.no_signal,
          weak: stats.weak,
          // Разбивка портов по PON-веткам: { '1': 16, '2': 8, ... }
          branches: stats.branches || {},
          // Для источника «недавно упали»: сколько упало на OLT и авария ли это.
          dropped: stats.dropped,
          severity: stats.severity === true,
        };
        groups.push(current);
      }
      current.rows.push(item);
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData, oltDescriptions, oltStats]);

  // Мобильная версия (карточки, сгруппированные по OLT)
  if (isMobile) {
    return (
      <Box>
        <Grid container spacing={2}>
          {groupedData.map((group) => {
            const collapsed = collapsedOlt.has(group.olt_ip);
            return (
              <Grid item xs={12} key={`grp-${group.olt_ip}`}>
                <Box
                  onClick={() => toggleOlt(group.olt_ip)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    // Аварийная ветка — красный фон.
                    backgroundColor: group.severity ? 'error.main' : 'primary.light',
                    color: 'primary.contrastText',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: group.severity ? 'error.dark' : 'primary.main' },
                  }}
                >
                  {collapsed ? (
                    <KeyboardArrowRightIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                    OLT: {group.olt_description}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${group.portCount != null ? group.portCount : group.rows.length} портов`}
                    sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }}
                  />
                  {group.branches && Object.keys(group.branches).length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'primary.contrastText', opacity: 0.9 }}
                    >
                      {formatBranches(group.branches)}
                    </Typography>
                  )}
                  {group.noSignal > 0 && (
                    <Chip
                      size="small"
                      color="error"
                      label={`нет сигнала: ${group.noSignal}`}
                      sx={{ height: 20, ml: 0.5 }}
                    />
                  )}
                  {group.weak > 0 && (
                    <Chip
                      size="small"
                      color="warning"
                      label={`слабый сигнал: ${group.weak}`}
                      sx={{ height: 20, ml: 0.5 }}
                    />
                  )}
                  {group.dropped > 0 && (
                    <Chip
                      size="small"
                      color={group.severity ? 'error' : 'default'}
                      label={`${group.severity ? '🔴 −' : '−'}${group.dropped} упавших`}
                      sx={{
                        height: 20,
                        ml: 0.5,
                        ...(group.severity ? { fontWeight: 'bold', animation: 'pulse 1.5s infinite' } : {}),
                      }}
                    />
                  )}
                </Box>
                {!collapsed &&
                  group.rows.map((row, index) => {
                    const oltStatus = getSignalStatus(row.signal_olt);
                    const onuStatus = getSignalStatus(row.signal_onu);
                    const key = `${row.olt_ip}-${row.port_number}`;
                    const lastActiveDate = lastActiveDates[key];
                    const stale = isSignalStale(row.date);

                    return (
                      <Card
                        key={`${key}-${index}`}
                        sx={{
                          cursor: 'pointer',
                          mb: 1,
                          '&:hover': { boxShadow: 3 },
                          ...(stale ? staleRowSx : {}),
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              {formatPortNumber(row.port_number)}
                            </Typography>
                            <IconButton size="small" color="primary">
                              <InfoIcon />
                            </IconButton>
                          </Box>

                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {row.port_description || 'Нет описания'}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mt: 2,
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                OLT
                              </Typography>
                              <Chip
                                size="small"
                                label={`${formatSignalValue(row.signal_olt).toFixed(2)} dBm`}
                                color={oltStatus.color}
                                icon={oltStatus.icon}
                                sx={{ height: 24, ml: 0.5 }}
                              />
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                ONU
                              </Typography>
                              <Chip
                                size="small"
                                label={`${formatSignalValue(row.signal_onu).toFixed(2)} dBm`}
                                color={onuStatus.color}
                                icon={onuStatus.icon}
                                sx={{ height: 24, ml: 0.5 }}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            <RelativeTime date={row.date} variant="caption" />
                          </Box>

                          {(formatSignalValue(row.signal_olt) === -0.4 ||
                            formatSignalValue(row.signal_onu) === -0.4) &&
                            lastActiveDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                Последняя активность: {formatAbsoluteDate(lastActiveDate)}
                              </Typography>
                            )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </Grid>
            );
          })}
        </Grid>

        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Строк:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />

        {selectedRow && (
          <SignalDetailView
            open={dialogOpen}
            onClose={handleCloseDialog}
            rowData={selectedRow}
          />
        )}
      </Box>
    );
  }

  // Десктопная версия (таблица со сворачиваемыми группами по OLT)
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
        <Table stickyHeader aria-label="signals table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Порт</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell align="center">Сигнал OLT (dBm)</TableCell>
              <TableCell align="center">Сигнал ONU (dBm)</TableCell>
              <TableCell>Обновлено</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedData.map((group) => {
              const collapsed = collapsedOlt.has(group.olt_ip);
              return (
                <React.Fragment key={`grp-${group.olt_ip}`}>
                  {/* Заголовок группы OLT — кликабельный, сворачивает/разворачивает.
                      Без MUI hover: его серый overlay делал текст нечитаемым на синем фоне. */}
                  <TableRow
                    onClick={() => toggleOlt(group.olt_ip)}
                    sx={{
                      cursor: 'pointer',
                      // Аварийная ветка (severity) — красный фон вместо синего.
                      backgroundColor: group.severity ? 'error.main' : 'primary.main',
                      '&:hover': { backgroundColor: group.severity ? 'error.dark' : 'primary.dark' },
                    }}
                  >
                    <TableCell colSpan={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {collapsed ? (
                          <KeyboardArrowRightIcon sx={{ color: 'primary.contrastText' }} />
                        ) : (
                          <KeyboardArrowDownIcon sx={{ color: 'primary.contrastText' }} />
                        )}
                        <Typography
                          variant="subtitle2"
                          sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}
                        >
                          {group.olt_description}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'primary.contrastText', opacity: 0.85 }}
                        >
                          {group.olt_ip}
                        </Typography>
                        <Chip
                          size="small"
                          label={`портов: ${group.portCount != null ? group.portCount : group.rows.length}`}
                          sx={{
                            height: 20,
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                          }}
                        />
                        {group.branches && Object.keys(group.branches).length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'primary.contrastText', opacity: 0.9 }}
                          >
                            {formatBranches(group.branches)}
                          </Typography>
                        )}
                        {group.noSignal > 0 && (
                          <Chip
                            size="small"
                            color="error"
                            label={`нет сигнала: ${group.noSignal}`}
                            sx={{ height: 20 }}
                          />
                        )}
                        {group.weak > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            label={`слабый сигнал: ${group.weak}`}
                            sx={{ height: 20 }}
                          />
                        )}
                        {/* Бейдж «недавно упали» для источника dropouts */}
                        {group.dropped > 0 && (
                          <Chip
                            size="small"
                            color={group.severity ? 'error' : 'default'}
                            label={`${group.severity ? '🔴 −' : '−'}${group.dropped} упавших`}
                            sx={{
                              height: 20,
                              ...(group.severity
                                ? { fontWeight: 'bold', animation: 'pulse 1.5s infinite' }
                                : {}),
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Строки данных группы (скрыты, если группа свёрнута) */}
                  {!collapsed &&
                    group.rows.map((row, index) => {
                      const oltStatus = getSignalStatus(row.signal_olt);
                      const onuStatus = getSignalStatus(row.signal_onu);
                      const key = `${row.olt_ip}-${row.port_number}`;
                      const lastActiveDate = lastActiveDates[key];
                      const stale = isSignalStale(row.date);
                      const isPortBoundary =
                        index > 0 &&
                        Math.floor((row.port_number - 16779009) / 256) !==
                          Math.floor(
                            (group.rows[index - 1].port_number - 16779009) / 256
                          );

                      return (
                        <TableRow
                          hover
                          onClick={() => handleRowClick(row)}
                          key={key}
                          sx={{
                            cursor: 'pointer',
                            ...(stale ? staleRowSx : {}),
                            ...(isPortBoundary
                              ? { borderTop: '1px dashed', borderColor: 'divider' }
                              : {}),
                          }}
                        >
                          <TableCell>{formatPortNumber(row.port_number)}</TableCell>
                          <TableCell>{row.port_description || 'Нет описания'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={`${formatSignalValue(row.signal_olt).toFixed(2)} dBm`}
                              color={oltStatus.color}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={`${formatSignalValue(row.signal_onu).toFixed(2)} dBm`}
                              color={onuStatus.color}
                            />
                          </TableCell>
                          <TableCell>
                            <RelativeTime date={row.date} />
                            {(formatSignalValue(row.signal_olt) === -0.4 ||
                              formatSignalValue(row.signal_onu) === -0.4) &&
                              lastActiveDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  посл. активность: {formatAbsoluteDate(lastActiveDate)}
                                </Typography>
                              )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalRows}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Строк на странице:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
      />

      {selectedRow && (
        <SignalDetailView
          open={dialogOpen}
          onClose={handleCloseDialog}
          rowData={selectedRow}
        />
      )}
    </Paper>
  );
};

export default SignalsTable;
